import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ListPlus } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchInput, SuggestionSearch } from "@/components/ui/search"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { useAppendChatHistoryMessage } from "@/modules/chats/hooks/useChatHistory"
import { ChatTemplateStoreData, fetchChatTemplates } from "@/modules/chats/services/fetchChatTemplates"
import { useChatInputStateGetter } from "@/modules/chats/stores/useChatInputStore"
import { AiTrackingMetadata } from "@/modules/chats/types/chat-message"
import { SearchOption } from "@/modules/patients/components/search-option"
import { ChatTemplateType, Country, Language, Service } from "@/types/__generated__/union-types"

interface ChatTemplatesBlockProps {
  appointmentLanguage: Language
  appointmentService: Service
  patientCountry: Country
}

const getServiceType = (serviceType: Service): ChatTemplateType => {
  return serviceType === "ADMINISTRATIVE_REQUEST" ? "ADMINISTRATIVE" : "MEDICAL"
}

export const ChatTemplatesBlock = ({
  appointmentLanguage,
  appointmentService,
  patientCountry,
}: ChatTemplatesBlockProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [chatTemplates, setChatTemplates] = useState<ChatTemplateStoreData[]>()

  useEffect(() => {
    if (!dropdownOpen || chatTemplates !== undefined) {
      return
    }

    let isOutdated = false

    const fetchChatTemplatesData = async () => {
      const data = await fetchChatTemplates(patientCountry)
      if (isOutdated) {
        return
      }
      const chatTemplate = data.get(appointmentLanguage)
      if (!chatTemplate) {
        setChatTemplates([])
        return
      }

      const chatTemplateService = getServiceType(appointmentService)
      const chatTemplatesData = chatTemplate
        .filter((item) => item.services.includes(chatTemplateService) && item.content)
        .sort((a, b) => a.title.localeCompare(b.title))

      setChatTemplates(chatTemplatesData)
    }
    fetchChatTemplatesData()

    return () => {
      isOutdated = true
    }
  }, [appointmentLanguage, appointmentService, patientCountry, setChatTemplates, dropdownOpen, chatTemplates])

  return (
    <div className="ml-auto flex shrink-0 flex-wrap justify-end gap-2">
      <DropdownMenu
        open={dropdownOpen}
        onOpenChange={(isOpen) => {
          setDropdownOpen(isOpen)
        }}
        modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="unstyle"
            data-testid="chat-templates-button"
            className="text-on-surface-subdued hover:bg-surface-hovered static right-2 p-2">
            <ListPlus className="text-on-surface-subdued h-6 w-6" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="rounded-lg" align="start">
          {chatTemplates ? (
            <ChatTemplatesSearch setDropdownOpen={setDropdownOpen} chatTemplates={chatTemplates ?? []} />
          ) : (
            <p className="p-5">Loading ...</p>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

interface ChatTemplatesSearchProps {
  setDropdownOpen: Dispatch<SetStateAction<boolean>>
  chatTemplates: ChatTemplateStoreData[]
}

const ChatTemplatesSearch = ({ setDropdownOpen, chatTemplates }: ChatTemplatesSearchProps) => {
  const { t } = useTranslation()

  const handleAppend = useAppendChatHistoryMessage()

  const getChatInputState = useChatInputStateGetter()

  const [searchValue, setSearchValue] = useState("")
  const normalizedSearchValue = searchValue.length >= 3 ? searchValue.trim().toLowerCase() : ""

  const setToChatInput = useCallback(
    (content: string, selectItemName: string) => {
      const { messageText, setAiTrackingMetadata } = getChatInputState()
      const newMessageText = messageText.trim().length === 0 ? content : messageText + "\n\n" + content

      handleAppend(newMessageText)

      FaroInsightsWrapper.pushEvent("chat_template_used", { templateName: selectItemName })

      const trackingMetadata: AiTrackingMetadata = {
        id: uuidv4(),
        name: "Chat templates",
        previousText: messageText,
        newText: selectItemName,
        selectedItem: selectItemName,
        createdAt: new Date().toISOString(),
      }

      setAiTrackingMetadata([trackingMetadata])
    },
    [getChatInputState, handleAppend],
  )

  const eligibleChatTemplates = chatTemplates.filter(
    (option) =>
      option.title.toLowerCase().includes(normalizedSearchValue) ||
      option.synonym?.toLowerCase().includes(normalizedSearchValue),
  )
  const hasSomeEligibleChatTemplates = eligibleChatTemplates.length > 0

  const handleSetChatInput = (option: ChatTemplateStoreData) => {
    setSearchValue("")
    setDropdownOpen(false)
    if (option.content) {
      setToChatInput(option.content, option.name)
    }
  }

  return (
    <SuggestionSearch>
      <div className="border-border border-b p-2">
        <SearchInput
          dataTestId="chat-templates"
          onKeyDown={(e) => e.stopPropagation()}
          value={searchValue}
          placeholder={t("common.search")}
          autoComplete="off"
          onChange={({ target: { value } }) => {
            setSearchValue(value)
          }}
          onClear={() => {
            setSearchValue("")
          }}
        />
      </div>
      {hasSomeEligibleChatTemplates ? (
        <ScrollArea className="grid max-w-88.25 gap-0.5" viewportClassName="max-h-84">
          <div className="py-4">
            {eligibleChatTemplates.map((option) => (
              <DropdownMenuSub key={option.name}>
                <DropdownMenuSubTrigger className="px-2 py-0">
                  <SearchOption
                    className="block w-84.25"
                    value={option.name}
                    searchValue={searchValue}
                    title={option.title}
                    synonym={option.synonym}
                    onSelect={() => handleSetChatInput(option)}
                  />
                </DropdownMenuSubTrigger>
              </DropdownMenuSub>
            ))}
          </div>
        </ScrollArea>
      ) : (
        <div className="text-on-surface-subdued relative cursor-default px-4 py-4 text-sm select-none">
          {t("common.nothingFound")}
        </div>
      )}
    </SuggestionSearch>
  )
}
